import { useMemo } from "react";

// FDI tooth numbering for adult dentition
// Upper row left-to-right: 18,17,16,15,14,13,12,11 | 21,22,23,24,25,26,27,28
// Lower row left-to-right: 48,47,46,45,44,43,42,41 | 31,32,33,34,35,36,37,38
const UPPER = [18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28];
const LOWER = [48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38];

// Image is 816x360. Usable horizontal range ≈ 8% → 95% (left/right labels at edges).
// Vertical: upper ≈ 5%–48%, lower ≈ 52%–95%.
const H_START = 8;     // percent
const H_END = 95;      // percent
const TOOTH_WIDTH = (H_END - H_START) / 16; // ~5.44%

const UPPER_TOP = 5;
const UPPER_HEIGHT = 43;
const LOWER_TOP = 52;
const LOWER_HEIGHT = 43;

const TOOTH_HIGHLIGHT_COLOR = "rgba(101, 93, 198, 0.45)";

const ToothChart = ({ selectedTeeth = [], onToggleTooth }) => {
  const selectedSet = useMemo(
    () => new Set(selectedTeeth.map(String)),
    [selectedTeeth]
  );
  const handleClick = (num) => onToggleTooth && onToggleTooth(String(num));

  const renderRow = (teethArr, topPct, heightPct) => (
    teethArr.map((num, idx) => {
      const leftPct = H_START + idx * TOOTH_WIDTH;
      const isSel = selectedSet.has(String(num));
      return (
        <button
          key={num}
          type="button"
          onClick={() => handleClick(num)}
          data-testid={`tooth-${num}`}
          aria-label={`السن رقم ${num}`}
          className="absolute group"
          style={{
            left: `${leftPct}%`,
            top: `${topPct}%`,
            width: `${TOOTH_WIDTH}%`,
            height: `${heightPct}%`,
            padding: 0,
            background: "transparent",
            border: "none",
            cursor: "pointer",
          }}
        >
          {/* Hover/selected highlight overlay */}
          <span
            className="absolute inset-1 rounded-lg transition-all duration-150 pointer-events-none"
            style={{
              background: isSel ? TOOTH_HIGHLIGHT_COLOR : "transparent",
              boxShadow: isSel ? "0 0 0 2px #655dc6" : "none",
            }}
          />
          <span
            className="absolute inset-1 rounded-lg transition-opacity duration-150 opacity-0 group-hover:opacity-100 pointer-events-none"
            style={{ background: "rgba(101, 93, 198, 0.18)" }}
          />
        </button>
      );
    })
  );

  return (
    <div className="bg-white rounded-xl p-3 sm:p-4 border border-slate-200" dir="rtl">
      <div className="flex items-center justify-between text-xs text-slate-500 mb-2 px-1">
        <span className="font-medium">الجانب الأيمن (R)</span>
        <span className="text-slate-400">اضغط على السن لإضافته/إزالته</span>
        <span className="font-medium">الجانب الأيسر (L)</span>
      </div>

      {/* Chart image + clickable overlays */}
      <div className="relative w-full" style={{ aspectRatio: "816 / 360" }}>
        <img
          src="/teeth-chart.jpg"
          alt="مخطط الأسنان"
          className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none"
          draggable={false}
        />
        {renderRow(UPPER, UPPER_TOP, UPPER_HEIGHT)}
        {renderRow(LOWER, LOWER_TOP, LOWER_HEIGHT)}
      </div>

      {selectedTeeth.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2">
          <span className="text-xs text-slate-500">الأسنان المختارة:</span>
          {selectedTeeth.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#655dc6] text-white text-xs font-semibold"
            >
              {t}
              <button
                type="button"
                onClick={() => handleClick(t)}
                className="hover:text-red-200 transition-colors"
                aria-label={`إزالة السن ${t}`}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default ToothChart;
